<template>
  <section class="product-safety-section">
    <ProductNutritionalSkeleton v-if="isLoading"/>
    <template v-else>
      <ProductSafetyHeader
        title="Besin Bilgileri ve Değerleri"
      />

      <transition name="fade">
        <div class="section-content">
          <NutritionInfo
            ref="nutritionInfoRef"
            :nutritionInfo="nutritionInfo"
            :isSaved="isSaved"
            @hasChanges="handleHasChanges"
            @saveChanges="handleSaveNutrition"
          />
          <NutritionRate
            ref="nutritionRateRef"
            :nutritionValuesRate="nutritionInfo"
            :isSaved="isSaved"
            @hasChanges="handleHasChanges"
            :onSaveChanges="handleSaveNutrition"
          />
        </div>
      </transition>
    </template>
  </section>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { sendProductDetailEvent } from '@/utils/ga4/helper'
import { GA4_EVENT } from '@/utils/ga4/event'
import ProductSafetyHeader from './ProductSafetyComponent/partial/ProductSafetyHeader.vue'
import NutritionRate from './ProductNutritionComponent/NutritionRate.vue'
import NutritionInfo from './ProductNutritionComponent/NutritionInfo.vue'
import ProductNutritionalSkeleton from '@/components/Skeleton/ProductDetailSkeleton/ProductNutritionalSkeleton.vue'

const props = defineProps({
  nutritionInfo: {
    type: Object,
    default: () => null
  },
  productId: {
    type: [String, Number],
    required: true
  },
  onSubmitAllSafetyInfo: {
    type: Function,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
})

const store = useStore()
const emit = defineEmits(['hasChanges'])
const nutritionInfoRef = ref(null)
const nutritionRateRef = ref(null)
const isSaved = ref(false)
const isInitialized = ref(false);

watch(
  () => props.nutritionInfo,
  async (newValue) => {
    isInitialized.value = false;
    isSaved.value = false;
    
    if (newValue != null) {
      await nextTick();
    }
    
    isInitialized.value = true;
  },
  { immediate: true }
);

const handleHasChanges = () => {
  if (!isInitialized.value) return;

  emit('hasChanges')
  isSaved.value = true;
  nutritionRateRef.value?.resetSuccessState?.();
};

const getCurrentNutritionInfo = () => {
  try {
    const nutritionInfoData = nutritionInfoRef.value?.getData() || {}
    const nutritionRateData = nutritionRateRef.value?.getData() || {}
    
    const nutritionValues = nutritionRateData.nutritionValues?.map(item => ({
      id: item.id || null,
      lookupCodeId: item.nutritionCode?.value,
      value: String(item.nutritionValue)
    })) || []
    
    return {
      allergenWarning: nutritionInfoData.allergenWarning || "",
      storageConditions: nutritionInfoData.storageConditions || "",
      usageRecommendations: nutritionInfoData.usageRecommendations || "",
      contents: nutritionInfoData.contents || "",
      expirationDate: nutritionInfoData.expirationDate || null,
      recommendedConsumptionDate: nutritionInfoData.recommendedConsumptionDate || null,
      nutritionValues: nutritionValues,
      originId:
        nutritionInfoData.origin?.value ??
        props.nutritionInfo?.origin?.value ??
        null
    }
  } catch (error) {
    console.error('Failed to get nutrition info:', error)
    return null
  }
}

const handleSaveNutrition = async () => {
  try {
    const payload = getCurrentNutritionInfo()
    
    if (!payload) {
      return
    }
    
    if (props.onSubmitAllSafetyInfo) {
      await props.onSubmitAllSafetyInfo(payload)
    } else {
      const nutritionRateData = nutritionRateRef.value?.getData() || {}
      const changesAll = nutritionRateData.applyToAllVariants !== undefined ? nutritionRateData.applyToAllVariants : true
      const response = await store.dispatch('products/safetyInfoSubmit', {
        productId: props.productId,
        payload: {
          changesAll,
          productSafetyRequest: {
            nutritionInfo: payload,
          },
        },
      });

      sendProductDetailEvent(GA4_EVENT.SAVE_CHANGES, {
        button_name: 'Değişiklikleri Kaydet',
        button_type: 'Save Changes',
        button_area: 'Safety Nutritional Information',
        item_id: props.productId,
        all_variants_checkbox: changesAll,
      })

      return response;
    }

    sendProductDetailEvent(GA4_EVENT.SAVE_CHANGES, {
      button_name: 'Değişiklikleri Kaydet',
      button_type: 'Save Changes',
      button_area: 'Safety Nutritional Information',
      item_id: props.productId,
      all_variants_checkbox: nutritionRateRef.value?.getData()?.applyToAllVariants ?? true,
    })
  } catch (error) {
    console.error('Failed to save nutrition info:', error)

    throw error
  }
}

const showSaveAllSuccessState = () => {
  isSaved.value = false;
  nutritionRateRef.value?.showSaveAllSuccessState?.();
};

defineExpose({ handleSaveNutrition, getCurrentNutritionInfo, showSaveAllSuccessState })
</script>

<style scoped>
.product-safety-section {
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  background: white;
}
.product-safety-section .safety-header{
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.top-row,
.info-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  height: 0;
}
</style>
